// pages/AccountSettings.tsx
import React, {useState, useEffect} from "react";
import {useNavigate, Link} from "react-router-dom";
import {useAuth} from "@/contexts/AuthContext";
import { getUserApi } from '@/lib/api/client';
import {
    Save,
    User as UserIcon,
    RefreshCw,
    Check,
    AlertCircle,
    ArrowLeft
} from "lucide-react";
import {cn} from "@/lib/utils";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {FormField} from "@/components/dashboard/candidate/profile/FormField";
import { ProfileAvatarUploader, IconChip, ActionButton } from "@/components/custom-ui";
import {AuthGuard} from "@/components/auth/AuthGuard";
import NotificationPreferences from "@/components/dashboard/NotificationPreferences";
import PasswordSettings from "@/components/dashboard/PasswordSettings";
import DeleteAccountSettings from "@/components/dashboard/DeleteAccountSettings";
import {NEXILINK_CARD_WHITE_ELEVATED} from "@/lib/nexilink-card-styles";

const AccountSettings: React.FC = () => {
    const navigate = useNavigate();
    const {user, getCurrentUser} = useAuth();

    // State management
    const [isLoading, setIsLoading] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Track scroll for header shadow
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // Form data
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: ""
    });

    // Load user data
    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.first_name || "",
                lastName: user.last_name || ""
            });
        }
    }, [user]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setShowError(false);

        try {
            await getUserApi().updateCurrentUser({
                first_name: formData.firstName,
                last_name: formData.lastName,
            });

            await getCurrentUser(true);

            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);

        } catch (error) {
            console.error("Failed to update account:", error);
            setErrorMessage("Det gick inte att spara ändringarna. Försök igen.");
            setShowError(true);
            setTimeout(() => setShowError(false), 5000);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="text-center">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-[#1A4D4F]"/>
                    <p className="text-gray-600">Laddar kontoinställningar...</p>
                </div>
            </div>
        );
    }

    return (
        <AuthGuard>
            <div className="min-h-screen bg-white">
                {/* Header - matches LandingPage style */}
                <header
                    className={cn(
                        "fixed top-0 left-0 right-0 z-40 bg-white transition-all duration-300",
                        scrolled ? "shadow-md border-b border-gray-100" : "border-b border-transparent"
                    )}
                >
                    <div className="flex items-center justify-between h-16 sm:h-[72px] px-4 sm:px-6 lg:px-8">
                        <Link to="/" className="flex-shrink-0">
                            <img
                                loading="lazy"
                                src="https://cdn.builder.io/api/v1/image/assets/89bc6265b1a84995b7b85b1453a49e93/e937596a79e27aedd1ca37344ef2209cd6e8c5da2cc900e6c0f5467b6176325e?apiKey=89bc6265b1a84995b7b85b1453a49e93&"
                                alt="Nexilink Logo"
                                className="h-8 sm:h-10 w-auto object-contain"
                            />
                        </Link>
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Tillbaka
                        </button>
                    </div>
                </header>

                {/* Content - offset for fixed header */}
                <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-8 space-y-6">
                    {/* Page header */}
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Kontoinställningar</h1>
                        <p className="text-gray-600 mt-1">Hantera dina grundläggande kontoinställningar</p>
                    </div>

                    {/* Success Alert */}
                    {showSuccess && (
                        <Alert className="bg-green-50 border-green-200">
                            <Check className="h-4 w-4 text-green-600"/>
                            <AlertTitle className="text-green-800">Kontot uppdaterat</AlertTitle>
                            <AlertDescription className="text-green-700">
                                Dina kontoinställningar har sparats framgångsrikt.
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Error Alert */}
                    {showError && (
                        <Alert className="bg-red-50 border-red-200">
                            <AlertCircle className="h-4 w-4 text-red-600"/>
                            <AlertTitle className="text-red-800">Fel</AlertTitle>
                            <AlertDescription className="text-red-700">
                                {errorMessage}
                            </AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Personal Information */}
                        <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6`}>
                            <div className="flex items-center gap-3 mb-6">
                                <IconChip icon={UserIcon} size="lg" />
                                <div className="min-w-0">
                                    <h2 className="text-lg font-semibold text-gray-900">Personuppgifter</h2>
                                    <p className="text-sm text-gray-500">Din profilbild och ditt namn</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {/* #249 — editable profile picture (account-level, /api/users/me). */}
                                <div className="pb-6 border-b border-gray-100">
                                    <ProfileAvatarUploader name={user?.full_name || ''} pictureUrl={user?.picture_url} />
                                </div>

                                {/* Email (read-only) */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">E-post</label>
                                    <div className="px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl text-gray-700">
                                        {user?.email || 'Ingen e-post angiven'}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                        label="Förnamn"
                                        id="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        placeholder="Ditt förnamn"
                                        required
                                    />

                                    <FormField
                                        label="Efternamn"
                                        id="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        placeholder="Ditt efternamn"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end">
                            <ActionButton type="submit" icon={Save} loading={isSaving} disabled={isSaving} className="px-8">
                                {isSaving ? 'Sparar...' : 'Spara ändringar'}
                            </ActionButton>
                        </div>
                    </form>

                    {/* #343 — the endpoint and the kit method both existed; nothing
                        reached them, so the only way to change a password was to log
                        out and use "Glömt lösenord". */}
                    <PasswordSettings />

                    <NotificationPreferences />

                    {/* Last on the page, on purpose — the destructive action should
                        be the thing you scroll past everything else to reach, not
                        something you meet on the way to changing your password.
                        Renders nothing at all for employers and admins. */}
                    <DeleteAccountSettings />
                </main>
            </div>
        </AuthGuard>
    );
};

export default AccountSettings;
